import React, { useState, useEffect, Suspense, lazy, useRef } from "react";
import { jwtDecode } from 'jwt-decode';
import { MessageSquare, X, CreditCard, User, Calendar, DollarSign } from "lucide-react";
import { API_BASE, logo } from "./constant";
const AiStudio = lazy(() => import("./components/AiStudio"));
const Page = lazy(() => import("./Pages/Page"));
const FirstTimeSetup = lazy(() => import("./Pages/FirstTimeSetup.jsx"));

// User Details Modal Component
const UserDetailsModal = ({ user, isOpen, onClose, authToken, isTokenExpired, handleTokenExpiration }) => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  useEffect(() => {
    if (isOpen && user && authToken) {
      fetchUserPayments();
    }
  }, [isOpen, user, authToken]);

  const fetchUserPayments = async () => {
    if (!user?._id) return;

    if (isTokenExpired(authToken)) {
      handleTokenExpiration('expired');
      return;
    }
    
    setLoadingPayments(true);
    try {
      const response = await fetch(
        `${API_BASE}/payments/getPaymentByUserId/${user._id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const err = new Error(errData.message || 'Failed to fetch user payments');
        err.status = response.status;
        err.errorCode = errData.errorCode;
        throw err;
      }

      const data = await response.json();
      if (data.success) {
        setPaymentHistory(data.data || []);
      }
    } catch (error) {
      if (error.status === 401) {
        handleTokenExpiration('api_401');
      } else {
        console.error("Error fetching user payments:", error);
      }
    } finally {
      setLoadingPayments(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <User className="w-6 h-6 mr-2" />
            User Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* User Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Email:</span>
                  <p className="text-gray-900">{user?.email}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Role:</span>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                      user?.user_role === "admin"
                        ? "bg-red-100 text-red-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {user?.user_role}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Credits:</span>
                  <p className="text-gray-900 font-semibold">{user?.credits}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Activity</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Joined:</span>
                  <p className="text-gray-900">
                    {new Date(user?.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Last Activity:</span>
                  <p className="text-gray-900">
                    {new Date(user?.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment History */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Payment History
              </h3>
            </div>
            
            {loadingPayments ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
                <span className="ml-2 text-gray-600">Loading payment history...</span>
              </div>
            ) : paymentHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paymentHistory.map((payment, index) => (
                      <tr key={payment._id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(payment.createdAt || payment.paymentDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ${payment.amount || payment.paymentAmount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {payment.paymentId || payment._id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            payment.status === 'completed' || payment.status === 'success'
                              ? 'bg-green-100 text-green-800'
                              : payment.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {payment.status || 'completed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No payment history found for this user</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced LoginPage component with simplified OTP functionality
const LoginPage = ({ 
  loginEmail, 
  setLoginEmail, 
  loginPassword, 
  setLoginPassword, 
  handleLogin, 
  loginError, 
  isLoading,
  NordGPTLogo,
  otpMode,
  setOtpMode,
  otp,
  setOtp,
  otpEmail,
  setOtpEmail,
  handleCreateOTP,
  handleVerifyOTP,
  otpSent,
  setOtpSent,
  otpLoading,
  sessionExpiredMessage
}) => {
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <NordGPTLogo size="w-20 h-20" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          {otpMode ? 'Admin OTP Login' : 'Sign in to your account'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Welcome to {process.env.REACT_APP_BRAND_NAME || ''} Admin Panel
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border border-gray-100">
          {loginError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
              {loginError}
            </div>
          )}

          {otpSent && (
            <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md text-sm">
              OTP sent to {otpEmail}! Check your email.
            </div>
          )}

          {sessionExpiredMessage && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md text-sm">
              ⚠️ {sessionExpiredMessage}
            </div>
          )}
          
          {!otpMode ? (
            // Regular Login Form
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    placeholder={(process.env.REACT_APP_BRAND_NAME || 'admin') + '@admin.com'}
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-slate-500 focus:border-slate-500 sm:text-sm"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-slate-500 focus:border-slate-500 sm:text-sm"
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleLogin}
                  disabled={isLoading || !loginEmail || !loginPassword}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>

              {/* OTP Login Option */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setOtpMode(true)}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-all duration-200"
                >
                  Admin OTP Login
                </button>
              </div>
            </div>
          ) : (
            // OTP Login Form
            <div className="space-y-6">
              {!otpSent ? (
                // Step 1: Enter Email and Generate OTP
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Admin Email Address
                    </label>
                    <div className="mt-1">
                      <input
                        type="email"
                        placeholder="Enter admin email"
                        value={otpEmail}
                        onChange={(e) => setOtpEmail(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleCreateOTP()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-slate-500 focus:border-slate-500 sm:text-sm"
                        autoComplete="email"
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      OTP will be sent to this email address
                    </p>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={handleCreateOTP}
                      disabled={otpLoading || !otpEmail}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      {otpLoading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending OTP...
                        </div>
                      ) : (
                        'Send OTP'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                // Step 2: Verify OTP
                <>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-4">
                      OTP sent to: <span className="font-medium">{otpEmail}</span>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Enter OTP
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleVerifyOTP()}
                        maxLength="6"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-slate-500 focus:border-slate-500 sm:text-sm text-center font-mono text-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={handleVerifyOTP}
                      disabled={otpLoading || !otp}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      {otpLoading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Verifying...
                        </div>
                      ) : (
                        'Verify OTP'
                      )}
                    </button>
                  </div>

                  {/* Resend OTP option */}
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp('');
                      }}
                      className="text-sm text-slate-600 hover:text-slate-800 underline"
                    >
                      Change email or resend OTP
                    </button>
                  </div>
                </>
              )}

              {/* Back to regular login */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => {
                    setOtpMode(false);
                    setOtp('');
                    setOtpEmail('');
                    setOtpSent(false);
                  }}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-all duration-200"
                >
                  Back to Regular Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AIToolsLayout = () => {
  // Apply runtime theme from platform_data
  useEffect(() => {
    const apply = (t={}) => {
      const root = document.documentElement;
      if (t.backgroundColor) root.style.setProperty('--app-bg', t.backgroundColor);
      if (t.formBackgroundColor) root.style.setProperty('--form-bg', t.formBackgroundColor);
      if (t.buttonBgColor) root.style.setProperty('--btn-bg', t.buttonBgColor);
      if (t.buttonTextColor) root.style.setProperty('--btn-text', t.buttonTextColor);
      document.body.classList.add('theme-apply');
    };
    const inferBackend = () => {
      try {
        const { protocol, hostname } = window.location;
        const parts = hostname.split('.');
        if (parts.length >= 2) {
          const domain = parts.slice(1).join('.');
          return `${protocol}//backend.${domain}`;
        }
      } catch {}
      return '';
    };
    (async () => {
      try {
        const base = inferBackend();
        if (!base) return;
        const r = await fetch(`${base}/api/platform-data`);
        const j = await r.json().catch(()=>({}));
        if (r.ok && j && j.data && j.data.theme) apply(j.data.theme);
        else document.body.classList.add('theme-apply');
      } catch {
        document.body.classList.add('theme-apply');
      }
    })();
  }, []);
  // Basic states
  const [starterMessages, setStarterMessages] = useState([
    { id: "1", question: "What's the weather like today?", priority: 1 },
    { id: "2", question: "Can you help me write a short story?", priority: 2 },
    {
      id: "3",
      question: "Explain quantum physics in simple terms",
      priority: 3,
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("Pages");
  const [dashboardTab, setDashboardTab] = useState("users"); // 'users' or 'payments'

  // Authentication states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [authToken, setAuthToken] = useState(
    localStorage.getItem("authToken") || ""
  );
  const [loginError, setLoginError] = useState("");

  // OTP states
  const [otpMode, setOtpMode] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Token validation states
  const [tokenExpiryWarningShown, setTokenExpiryWarningShown] = useState(false);
  const tokenCheckIntervalRef = useRef(null);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");

  // Dashboard states
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({});
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  // Sync branding helper: writes .env.local for Admin/User, optionally runs startup script
  const handleSyncBranding = async () => {
    try {
      const base = (API_BASE || '').replace(/\/api\/?$/, '');
      if (!base) {
        alert('Backend base URL is not set.');
        return;
      }
      const resp = await fetch(`${base}/api/setup/sync-frontend-envs`, { method: 'POST' });
      const js = await resp.json().catch(() => ({}));
      if (!resp.ok || js.success === false) throw new Error(js.message || 'Sync failed');
      const doRestart = (typeof window !== 'undefined') && window.confirm('Branding synced. Run startup script to rebuild and restart now?');
      if (doRestart) {
        const r = await fetch(`${base}/api/setup/startup/run`, { method: 'POST' });
        const jr = await r.json().catch(() => ({}));
        if (!r.ok || jr.success === false) throw new Error(jr.message || 'Failed to trigger restart');
        alert('Startup script triggered. Please wait and refresh.');
      } else {
        alert('Branding synced. Please rebuild/restart frontends to apply.');
      }
    } catch (e) {
      alert(e.message || 'Branding sync failed');
    }
  };

  // Payment states
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Only show these sections after login - removed Account
  const navigationItems = [
    { name: "Pages", icon: "📄" },
    { name: "Dashboard", icon: "📊" },
    { name: "AI Studio", icon: "🎨" },
    { name: "Setup", icon: "⚙️"  }
  ];

  const loadingIcons = [
    {
      id: "spinner",
      symbol: "↻",
      component: <div className="animate-spin">↻</div>,
    },
    {
      id: "dots",
      symbol: "●●●",
      component: (
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"></div>
          <div
            className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
            style={{ animationDelay: "0.1s" }}
          ></div>
          <div
            className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
            style={{ animationDelay: "0.2s" }}
          ></div>
        </div>
      ),
    },
    {
      id: "pulse",
      symbol: "○",
      component: (
        <div className="w-3 h-3 bg-gray-600 rounded-full animate-pulse"></div>
      ),
    },
    {
      id: "bars",
      symbol: "▌▌▌",
      component: (
        <div className="flex space-x-1">
          <div className="w-1 h-4 bg-gray-600 animate-pulse"></div>
          <div
            className="w-1 h-4 bg-gray-600 animate-pulse"
            style={{ animationDelay: "0.1s" }}
          ></div>
          <div
            className="w-1 h-4 bg-gray-600 animate-pulse"
            style={{ animationDelay: "0.2s" }}
          ></div>
        </div>
      ),
    },
  ];

  // Token validation utility functions
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return false;
      return Date.now() >= decoded.exp * 1000;
    } catch (error) {
      return true;
    }
  };

  const getTokenExpiryTime = (token) => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp;
    } catch (error) {
      return null;
    }
  };

  const handleTokenExpiration = (reason = 'expired') => {
    handleLogout();
    let message = "Your session has expired. Please log in again.";
    if (reason === 'invalid') {
      message = "Your session is invalid. Please log in again.";
    } else if (reason === 'api_401') {
      message = "Your session has expired. Please log in again to continue.";
    }
    setSessionExpiredMessage(message);
  };

  // Periodic token validation
  useEffect(() => {
    if (!isLoggedIn || !authToken) {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
        tokenCheckIntervalRef.current = null;
      }
      return;
    }

    const interval = setInterval(() => {
      if (isTokenExpired(authToken)) {
        handleTokenExpiration('expired');
        clearInterval(interval);
        tokenCheckIntervalRef.current = null;
        return;
      }

      const exp = getTokenExpiryTime(authToken);
      if (exp) {
        const minutesUntilExpiry = (exp * 1000 - Date.now()) / 60000;
        if (minutesUntilExpiry < 5 && !tokenExpiryWarningShown) {
          alert("Your session will expire in less than 5 minutes. Please save your work.");
          setTokenExpiryWarningShown(true);
        }
      }
    }, 60000); // Check every 60 seconds

    tokenCheckIntervalRef.current = interval;

    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
        tokenCheckIntervalRef.current = null;
      }
    };
  }, [isLoggedIn, authToken, tokenExpiryWarningShown]);

  // Check authentication on component mount
  useEffect(() => {
    if (authToken) {
      if (isTokenExpired(authToken)) {
        handleTokenExpiration('expired');
        return;
      }
      try {
        const decoded = jwtDecode(authToken);
        if (!decoded?.email) {
          handleTokenExpiration('invalid');
          return;
        }
        setIsLoggedIn(true);
        setUser({ email: decoded.email });
      } catch (error) {
        handleTokenExpiration('invalid');
      }
    }
  }, [authToken]);

  // Load data when dashboard is active and user is logged in
  useEffect(() => {
    if (activeSection === "Dashboard" && isLoggedIn && authToken) {
      if (dashboardTab === "users") {
        fetchUsers();
      } else if (dashboardTab === "payments") {
        fetchPayments();
      }
    }
  }, [activeSection, dashboardTab, isLoggedIn, authToken]);

  // API Functions
  const handleLogin = async () => {
    setLoginError("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: loginEmail,
          password: loginPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const token = data.data.token;
        setAuthToken(token);
        localStorage.setItem("authToken", token);
        const decoded = jwtDecode(token);
        setUser({ email: decoded.email });
        setIsLoggedIn(true);
        setTokenExpiryWarningShown(false);
        setSessionExpiredMessage("");
        setLoginEmail("");
        setLoginPassword("");
      } else {
        setLoginError(data.message || "Login failed");
      }
    } catch (error) {
      setLoginError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // OTP Functions
  const handleCreateOTP = async () => {
    if (!otpEmail.trim()) {
      setLoginError("Please enter an email address");
      return;
    }

    setLoginError("");
    setOtpLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/admin/create-otp-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOtpSent(true);
      } else {
        setLoginError(data.message || "Failed to send OTP");
      }
    } catch (error) {
      setLoginError("Network error. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setLoginError("Please enter the OTP");
      return;
    }

    setLoginError("");
    setOtpLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/admin/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          otp: otp,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const token = data.data.token;
        setAuthToken(token);
        localStorage.setItem("authToken", token);
        const decoded = jwtDecode(token);
        setUser({ email: decoded.email });
        setIsLoggedIn(true);
        setTokenExpiryWarningShown(false);
        setSessionExpiredMessage("");
        // Reset OTP states
        setOtpMode(false);
        setOtp("");
        setOtpEmail("");
        setOtpSent(false);
      } else {
        setLoginError(data.message || "OTP verification failed");
      }
    } catch (error) {
      setLoginError("Network error. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setIsLoggedIn(false);
    setAuthToken("");
    localStorage.removeItem("authToken");
    setLoginEmail("");
    setLoginPassword("");
    setActiveSection("Pages"); // Reset to default section
    // Reset OTP states
    setOtpMode(false);
    setOtp("");
    setOtpEmail("");
    setOtpSent(false);
    // Clear token validation states
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
      tokenCheckIntervalRef.current = null;
    }
    setTokenExpiryWarningShown(false);
  };

  const fetchUsers = async () => {
    if (!authToken) return;

    if (isTokenExpired(authToken)) {
      handleTokenExpiration('expired');
      return;
    }

    setUsersLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}/auth/admin/users?page=1&limit=50&sortBy=createdAt&sortOrder=desc`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const err = new Error(errData.message || 'Failed to fetch users');
        err.status = response.status;
        err.errorCode = errData.errorCode;
        throw err;
      }

      const data = await response.json();

      if (data.success) {
        setUsers(data.data.users);
        setUsersPagination(data.data.pagination);
      }
    } catch (error) {
      if (error.status === 401) {
        handleTokenExpiration('api_401');
      } else {
        console.error("Error fetching users:", error);
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchPayments = async () => {
    if (!authToken) return;

    if (isTokenExpired(authToken)) {
      handleTokenExpiration('expired');
      return;
    }

    setPaymentsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/payments`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const err = new Error(errData.message || 'Failed to fetch payments');
        err.status = response.status;
        err.errorCode = errData.errorCode;
        throw err;
      }

      const data = await response.json();

      if (data.success) {
        // Sort payments by date (latest first)
        const sortedPayments = (data.data || []).sort((a, b) => 
          new Date(b.createdAt || b.paymentDate) - new Date(a.createdAt || a.paymentDate)
        );
        setPayments(sortedPayments);
      }
    } catch (error) {
      if (error.status === 401) {
        handleTokenExpiration('api_401');
      } else {
        console.error("Error fetching payments:", error);
      }
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

const NordGPTLogo = ({ size = "w-8 h-8", textColor = "text-white" }) => (
    <div className="flex items-center space-x-2">
      <div className={`${size} bg-slate-600 rounded-lg flex items-center justify-center`}>
        <img src={logo} alt={(process.env.REACT_APP_BRAND_NAME || 'Brand') + ' Logo'} />
      </div>
    </div>
);

  // If not logged in, show login page
  if (!isLoggedIn) {
    return (
      <LoginPage
        loginEmail={loginEmail}
        setLoginEmail={setLoginEmail}
        loginPassword={loginPassword}
        setLoginPassword={setLoginPassword}
        handleLogin={handleLogin}
        loginError={loginError}
        isLoading={isLoading}
        NordGPTLogo={NordGPTLogo}
        otpMode={otpMode}
        setOtpMode={setOtpMode}
        otp={otp}
        setOtp={setOtp}
        otpEmail={otpEmail}
        setOtpEmail={setOtpEmail}
        handleCreateOTP={handleCreateOTP}
        handleVerifyOTP={handleVerifyOTP}
        otpSent={otpSent}
        setOtpSent={setOtpSent}
        otpLoading={otpLoading}
        sessionExpiredMessage={sessionExpiredMessage}
      />
    );
  }

  // Main authenticated layout
  return (
    <div className="flex h-screen bg-gray-100 text-gray-900 font-sans">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 shadow-md flex flex-col">
        <div className="p-4 bg-slate-600 text-white">
          <div className="flex items-center space-x-2">
            <NordGPTLogo size="w-16 h-16" textColor="text-white" />
          </div>
        </div>

        <div className="px-3 pb-2 pt-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Navigation
          </h3>
        </div>

        <nav className="flex-1 px-3 pb-4 overflow-y-auto">
          <ul className="space-y-1">
            {navigationItems.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => setActiveSection(item.name)}
                  className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-md transition-all duration-200 ${
                    activeSection === item.name
                      ? "bg-gray-100 text-gray-800 border-l-2 border-gray-500"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-800"
                  }`}
                >
                  <span className="text-base">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User info and logout */}
        <div className="p-3 border-t border-gray-200">
          <div className="mb-3 text-sm text-gray-600">
            <div className="font-medium">{user?.email}</div>
          </div>
          <button 
            onClick={handleLogout}
            className="w-full bg-red-600 text-white py-2 px-3 rounded-md hover:bg-red-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-gray-50">
        <div className="bg-white border-b border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-gray-800">
              <MessageSquare className="w-5 h-5" />
              <span className="font-semibold text-lg">{activeSection}</span>
            </div>
          </div>
        </div>

        {activeSection === "AI Studio" ? (
          <Suspense fallback={<div className="p-6">Loading…</div>}>
            <AiStudio authToken={authToken} />
          </Suspense>
        ) : activeSection === "Dashboard" ? (
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-6xl mx-auto">
              {/* Dashboard Tabs */}
              <div className="mb-6">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setDashboardTab("users")}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                        dashboardTab === "users"
                          ? "border-slate-500 text-slate-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <User className="w-4 h-4 inline mr-2" />
                      User Management
                    </button>
                    <button
                      onClick={() => setDashboardTab("payments")}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                        dashboardTab === "payments"
                          ? "border-slate-500 text-slate-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <DollarSign className="w-4 h-4 inline mr-2" />
                      Monetization
                    </button>
                  </nav>
                </div>
              </div>

              {dashboardTab === "users" ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    User Management
                  </h2>
                  <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
                    {usersLoading ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
                        <span className="ml-2 text-gray-600">Loading users...</span>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Role
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Credits
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Joined
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Activity
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((user) => (
                              <tr 
                                key={user._id} 
                                className="hover:bg-gray-50 cursor-pointer transition-colors"
                                onClick={() => handleUserClick(user)}
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {user.email}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      user.user_role === "admin"
                                        ? "bg-red-100 text-red-800"
                                        : "bg-blue-100 text-blue-800"
                                    }`}
                                  >
                                    {user.user_role}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {user.credits}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(user.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(user.updatedAt).toLocaleDateString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {usersPagination.total > 0 && (
                      <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-700">
                            Showing page {usersPagination.page} of{" "}
                            {usersPagination.pages} ({usersPagination.total} total
                            users)
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Payment History & Monetization
                  </h2>
                  <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
                    {paymentsLoading ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
                        <span className="ml-2 text-gray-600">Loading payments...</span>
                      </div>
                    ) : payments.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Payment Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                User Email
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Payment ID
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {payments.map((payment, index) => (
                              <tr key={payment._id || index} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {new Date(payment.createdAt || payment.paymentDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {payment.userEmail || payment.email || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                                  ${payment.amount || payment.paymentAmount}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                  {payment.paymentId || payment._id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    payment.status === 'completed' || payment.status === 'success'
                                      ? 'bg-green-100 text-green-800'
                                      : payment.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {payment.status || 'completed'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Payment Summary */}
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                              Total Payments: {payments.length}
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                              Total Revenue: ${payments.reduce((sum, payment) => 
                                sum + (parseFloat(payment.amount || payment.paymentAmount || 0)), 0
                              ).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No payments found</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          
        ) : activeSection === "Setup" ? (
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-5xl mx-auto">
              <Suspense fallback={<div className="p-6">Loading…</div>}>
                <FirstTimeSetup mode="edit" />
              </Suspense>
            </div>
          </div>
        ) : (
          /* Pages Section */
          <Suspense fallback={<div className="p-6">Loading…</div>}>
            <Page
              authToken={authToken}
              isLoggedIn={isLoggedIn}
            />
          </Suspense>
        )}
      </div>

      {/* User Details Modal */}
      <UserDetailsModal
        user={selectedUser}
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        authToken={authToken}
        isTokenExpired={isTokenExpired}
        handleTokenExpiration={handleTokenExpiration}
      />

      {/* Sync action button removed to restore original visuals */}
    </div>
  );
};

export default AIToolsLayout;
